import useAlert from '@/composables/Alert'
import {
  AlertKind,
  AsyncState,
  CreateAsyncStateOptions,
  FetchAsyncStateOptions,
} from './interfaces/AsyncState'
import { cloneAsyncState, createAsyncState, resetAsyncState } from './asyncState'
import { fetchAsyncState as fetchAsyncStateCore } from './fetchAsyncState'

/**
 * Único `use*` da camada — e o prefixo se paga aqui, porque este é o único
 * ponto que depende de escopo de `setup` (chama `useAlert`).
 *
 * `createAsyncState`, `resetAsyncState`, `cloneAsyncState` e `fetchAsyncState`
 * são funções livres e podem ser importadas diretamente por quem não quer o
 * alerta nem a dependência de escopo.
 */
export default function useAsyncState() {
  const { showTimeAlert } = useAlert()

  const onAlert = (message: string, kind: AlertKind) => showTimeAlert(message, kind)

  function fetchState<T>(
    state: AsyncState<T>,
    callback: () => Promise<T>,
    alert: AlertKind | null = null,
    customErrorMsg: string | null | FetchAsyncStateOptions = null,
  ): Promise<void> {
    const options: FetchAsyncStateOptions =
      customErrorMsg !== null && typeof customErrorMsg === 'object'
        ? customErrorMsg
        : { customErrorMsg }

    return fetchAsyncStateCore<T>(state, callback, alert, { onAlert, ...options })
  }

  function create<T>(initData: T, options?: CreateAsyncStateOptions) {
    return createAsyncState<T>(initData, options)
  }

  function reset<T>(state: AsyncState<T>, initData: T, loading = false) {
    return resetAsyncState<T>(state, initData, loading)
  }

  function clone<T>(state: AsyncState<T>, options?: CreateAsyncStateOptions) {
    return cloneAsyncState<T>(state, options)
  }

  return {
    createAsyncState: create,
    resetAsyncState: reset,
    cloneAsyncState: clone,
    fetchAsyncState: fetchState,
  }
}
